import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from 'react';

import { cn } from '../base/utils';

export interface WorkbenchContentPaneClassNames {
  root?: string;
  header?: string;
  body?: string;
  footer?: string;
}

export interface WorkbenchContentPaneBodyProps extends Omit<ComponentPropsWithoutRef<'div'>, 'children' | 'className'> {
  className?: string;
}

export interface WorkbenchContentPaneProps extends Omit<ComponentPropsWithoutRef<'section'>, 'children' | 'className' | 'style'> {
  as?: ElementType;
  bodyAs?: ElementType;
  header?: ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  bodyProps?: WorkbenchContentPaneBodyProps;
  classNames?: WorkbenchContentPaneClassNames;
  style?: CSSProperties;
  [key: string]: any;
}

function shouldRenderBodyWrapper(props: {
  header?: ReactNode;
  footer?: ReactNode;
  bodyAs?: ElementType;
  bodyClassName?: string;
  bodyProps?: WorkbenchContentPaneBodyProps;
  classNames?: WorkbenchContentPaneClassNames;
}) {
  return Boolean(
    props.header ||
      props.footer ||
      props.bodyAs ||
      props.bodyClassName ||
      props.bodyProps ||
      props.classNames?.body,
  );
}

export const WorkbenchContentPane = forwardRef<HTMLElement, WorkbenchContentPaneProps>(
  function WorkbenchContentPane(
    {
      as: Component = 'section',
      bodyAs: BodyComponent = 'div',
      header,
      footer,
      children,
      className,
      headerClassName,
      bodyClassName,
      footerClassName,
      bodyProps,
      classNames,
      style,
      ...props
    },
    ref,
  ) {
    const renderBodyWrapper = shouldRenderBodyWrapper({
      header,
      footer,
      bodyAs: BodyComponent === 'div' ? undefined : BodyComponent,
      bodyClassName,
      bodyProps,
      classNames,
    });
    const { className: bodyPropsClassName, ...restBodyProps } = bodyProps ?? {};

    return (
      <Component
        ref={ref}
        data-workbench-content-pane
        className={cn(
          'workbench-surface-subtle flex h-full min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-border/50 transition-all duration-300 dark:border-border/40',
          classNames?.root,
          className,
        )}
        style={style}
        {...props}
      >
        {header ? (
          <div className={cn('shrink-0 border-b border-border/40 px-4 py-3 dark:border-border/30', classNames?.header, headerClassName)}>
            {header}
          </div>
        ) : null}
        {renderBodyWrapper ? (
          <BodyComponent
            className={cn('min-h-0 flex-1 overflow-hidden', classNames?.body, bodyClassName, bodyPropsClassName)}
            {...restBodyProps}
          >
            {children}
          </BodyComponent>
        ) : (
          children
        )}
        {footer ? (
          <div className={cn('shrink-0 border-t border-border/40 px-4 py-3 dark:border-border/30', classNames?.footer, footerClassName)}>
            {footer}
          </div>
        ) : null}
      </Component>
    );
  },
);
